var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import BaseTable, { AutoResizer } from 'react-base-table';
import 'react-base-table/styles.css';
import classNames from 'classnames';
import { SortableContainer, SortableElement } from 'react-sortable-hoc';
import { makeStyles } from "../../styles/index";
import { useMeasure } from "../../hooks/useMeasure";
import { useColumns } from "./hooks/useColumns";
import { reorderList } from "./utils/reorderList";
import { getNextOrder } from "./utils/getNextOrder";
import { DataListTableHeaderRow } from "./DataListTableHeaderRow";
import { DataListTableRow } from "./DataListTableRow";
import { DataListTableHeaderCell } from "./DataListTableHeaderCell";
import { DataListTableCell } from "./DataListTableCell";
var DEFAULT_ROW_HEIGHT = 50;
var DEFAULT_HEADER_HEIGHT = 50;
var DEFAULT_END_REACHED_THRESHOLD = 500;
// Draggable用のコンポーネント定義
var DraggableContainer = SortableContainer(function (_a) {
    var children = _a.children;
    return children;
});
var DraggableElement = SortableElement(function (_a) {
    var children = _a.children;
    return children;
});
var styles = function (theme) { return ({
    root: {
        width: '100%',
        height: '100%',
        '& .BaseTable__row-cell, .BaseTable__header-cell': {
            borderRight: theme.shape.borderWidth.thin + "px solid " + theme.palette.light.text.hint,
        },
        '& .BaseTable__column-resizer': {
            width: 10,
        },
        // デフォルトの不要なスタイルを無効化する
        '& .BaseTable': {
            fontSize: 'inherit',
            boxShadow: 'none',
        },
        '& .BaseTable__row, .BaseTable__header-row': {
            border: 'none',
            backgroundColor: 'transparent',
        },
        '& .BaseTable__table-frozen-left': {
            boxShadow: 'none',
        },
        '& .BaseTable__resizing-line': {
            display: 'none',
        },
        '& .BaseTable__table-main': {
            outline: 'none',
        },
        // 固定行の最後に線を引く
        '& .BaseTable__row--frozen:last-child': {
            borderBottom: function (props) { var _a; return (_a = props.frozenRowBoundary) !== null && _a !== void 0 ? _a : "2px solid " + theme.palette.common.black; },
        },
    },
    blankFilling: {
        // ライブラリ内でstyleで当てているため、上書きするためにimportantを指定する
        '& .BaseTable__body': {
            overflowY: 'hidden !important',
        },
        '& .BaseTable__body > div, .BaseTable__row': {
            minWidth: '100% !important',
        },
    },
    scrollbar: {
        // ライブラリ内でstyleで当てているため、上書きするためにimportantを指定する
        '& .BaseTable__body': {
            overflowX: 'overlay !important',
        },
        '& .BaseTable__body::-webkit-scrollbar': {
            width: 10,
            height: 10,
        },
        '& .BaseTable__body::-webkit-scrollbar-thumb': {
            color: 'rgba(0, 0, 0, 0.4)',
            boxShadow: 'inset 0 0 0 10px',
            borderRadius: 13,
            border: 'solid transparent 2px',
            '&:hover': {
                color: 'rgba(0, 0, 0, .7)',
            },
        },
    },
    notBlankFillingScrollbar: {
        // ライブラリ内でstyleで当てているため、上書きするためにimportantを指定する
        '& .BaseTable__body': {
            overflowY: 'overlay !important',
        },
    },
}); };
var useStyles = makeStyles(styles, { name: 'SnDataListTable' });
export var DataListTable = function (props) {
    var _a;
    var outerColumns = props.columns, rows = props.rows, _b = props.headerHeight, headerHeight = _b === void 0 ? DEFAULT_HEADER_HEIGHT : _b, _c = props.rowHeight, rowHeight = _c === void 0 ? DEFAULT_ROW_HEIGHT : _c, sortCondition = props.sortCondition, fixedRowIndex = props.fixedRowIndex, selectedList = props.selectedList, onSelect = props.onSelect, onToggleAllSelect = props.onToggleAllSelect, onSortChange = props.onSortChange, onDragEndRow = props.onDragEndRow, onDoubleClick = props.onDoubleClick, onEndReached = props.onEndReached, scrollToTargetRow = props.scrollToTargetRow, endReachedThresholdRows = props.endReachedThresholdRows, scrollResetKey = props.scrollResetKey, scrollbarVisible = props.scrollbarVisible;
    var _d = useColumns(outerColumns, props), columns = _d.columns, enabledDraggableRow = _d.enabledDraggableRow;
    var _e = useState(null), tableSize = _e[0], setTableSize = _e[1];
    var tableRef = useRef(null);
    var classes = useStyles(props);
    var measureStyle = useMeasure(props).style;
    var _f = useMemo(function () {
        // eslint-disable-next-line no-shadow -- 外にそのまま渡すので同じ名前にしておく
        var frozenRows = rows.filter(function (_a) {
            var frozen = _a.frozen;
            return frozen;
        });
        var unfrozenRows = rows.filter(function (_a) {
            var frozen = _a.frozen;
            return !frozen;
        });
        if (!tableSize) {
            // 空レコード適用に必要な情報が無い場合はそのまま表示する
            return {
                renderingRows: unfrozenRows,
                frozenRows: frozenRows,
                hasBlankFilling: false,
            };
        }
        var rowsTotalHeight = rowHeight * rows.length;
        var blankRowCount = Math.max(0, Math.ceil((tableSize.height - headerHeight - rowsTotalHeight) / rowHeight));
        if (!blankRowCount) {
            // 空レコードが必要無い場合はそのまま表示する
            return {
                renderingRows: unfrozenRows,
                frozenRows: frozenRows,
                hasBlankFilling: false,
            };
        }
        var blankRows = [];
        for (var i = 0; i < blankRowCount; i += 1) {
            blankRows.push({
                id: "blank" + i,
                blank: true,
            });
        }
        return {
            renderingRows: unfrozenRows.concat(blankRows),
            frozenRows: frozenRows,
            hasBlankFilling: true,
        };
    }, [headerHeight, rowHeight, rows, tableSize]), renderingRows = _f.renderingRows, frozenRows = _f.frozenRows, hasBlankFilling = _f.hasBlankFilling;
    useEffect(function () {
        if (tableRef.current) {
            tableRef.current.scrollToPosition({ scrollLeft: 0, scrollTop: 0 });
        }
    }, [scrollResetKey]);
    // scrollToTargetRowの値の変更を検知したらその番目の行までスクロールさせる
    useEffect(function () {
        if (scrollToTargetRow && tableRef.current) {
            tableRef.current.scrollToRow(scrollToTargetRow, 'end');
        }
    }, [scrollToTargetRow]);
    useEffect(function () {
        if (hasBlankFilling && tableRef.current) {
            // hasBlankFillingがfalseからtrueになったとき
            // blankFillingで追加した最後の行のテーブル枠外にはみ出た"余り"の部分にスクロールしないようにリセットを行う
            tableRef.current.scrollToTop(0);
        }
    }, [hasBlankFilling]);
    // react-base-table独自のイベントハンドラー
    var rowEventHandlers = {
        onClick: function (_a) {
            var rowIndex = _a.rowIndex, rowData = _a.rowData, event = _a.event;
            if (rowData.blank) {
                // blankのレコードは無視する
                return;
            }
            switch (event.detail) {
                case 2:
                    // 2回クリックの時のみダブルクリック扱いにする
                    onDoubleClick === null || onDoubleClick === void 0 ? void 0 : onDoubleClick(rowIndex);
                    break;
                default:
                    onSelect === null || onSelect === void 0 ? void 0 : onSelect(rowIndex);
                    break;
            }
        },
    };
    // ソート表示の更新
    var handleSortChange = useCallback(function (key) {
        onSortChange === null || onSortChange === void 0 ? void 0 : onSortChange({
            key: key,
            order: getNextOrder(key, sortCondition),
        });
    }, [onSortChange, sortCondition]);
    var _g = useMemo(function () {
        return {
            isAllSelected: !(selectedList === null || selectedList === void 0 ? void 0 : selectedList.includes(false)),
            getSelected: function (rowIndex) {
                return selectedList && selectedList[rowIndex];
            },
        };
    }, [selectedList]), isAllSelected = _g.isAllSelected, getSelected = _g.getSelected;
    // ヘッダーの描画用
    var headerRenderer = useCallback(function (_a) {
        var cells = _a.cells, headerRendererColumns = _a.columns;
        return (React.createElement(DataListTableHeaderRow, { cells: cells, columns: headerRendererColumns, onSortChange: handleSortChange }));
    }, [handleSortChange]);
    // レコードの描画用
    var rowRenderer = useCallback(function (_a) {
        var cells = _a.cells, rowRendererColumns = _a.columns, rowData = _a.rowData, rowIndex = _a.rowIndex;
        return enabledDraggableRow ? (React.createElement(DraggableElement, { index: rowIndex },
            React.createElement(DataListTableRow, { cells: cells, columns: rowRendererColumns, rowData: rowData, rowIndex: rowIndex, selected: getSelected(rowIndex), fixedRowIndex: fixedRowIndex }))) : (React.createElement(DataListTableRow, { cells: cells, columns: rowRendererColumns, rowData: rowData, rowIndex: rowIndex, selected: getSelected(rowIndex), fixedRowIndex: fixedRowIndex }));
    }, [enabledDraggableRow, fixedRowIndex, getSelected]);
    // セル描画用
    var components = useMemo(function () {
        return {
            TableCell: function (cellProps) {
                return (React.createElement(DataListTableCell, { cellProps: cellProps, selected: getSelected(cellProps.rowIndex), handleSize: rowHeight }));
            },
            TableHeaderCell: function (cellProps) {
                return (React.createElement(DataListTableHeaderCell, { cellProps: cellProps, sortCondition: sortCondition, isAllSelected: isAllSelected, onToggleAllSelect: onToggleAllSelect }));
            },
        };
    }, [getSelected, isAllSelected, onToggleAllSelect, rowHeight, sortCondition]);
    // Draggable用の定義
    var getContainer = useCallback(function () {
        var _a, _b;
        return (_b = (_a = tableRef === null || tableRef === void 0 ? void 0 : tableRef.current) === null || _a === void 0 ? void 0 : _a.getDOMNode()) === null || _b === void 0 ? void 0 : _b.querySelector('.BaseTable__body');
    }, []);
    var getHelperContainer = useCallback(function () {
        var _a, _b;
        return (_b = (_a = tableRef === null || tableRef === void 0 ? void 0 : tableRef.current) === null || _a === void 0 ? void 0 : _a.getDOMNode()) === null || _b === void 0 ? void 0 : _b.querySelector('.BaseTable__table');
    }, []);
    var handleDragSortEnd = useCallback(function (_a) {
        var _b;
        var oldIndex = _a.oldIndex, newIndex = _a.newIndex;
        onDragEndRow === null || onDragEndRow === void 0 ? void 0 : onDragEndRow(reorderList(rows, oldIndex, newIndex), selectedList ? reorderList(selectedList, oldIndex, newIndex) : undefined);
        if (hasBlankFilling) {
            // 最後までドラッグさせると余剰描画分の空レコードと入れ替わりスクロールしてしまうため、元に戻す
            (_b = tableRef === null || tableRef === void 0 ? void 0 : tableRef.current) === null || _b === void 0 ? void 0 : _b.scrollToTop(0);
        }
    }, [hasBlankFilling, onDragEndRow, rows, selectedList]);
    // 再読み込みを行う高さを求める
    var endReachedThreshold = useMemo(function () {
        if (endReachedThresholdRows === 0) {
            // 残りレコード指定が0の場合は計算せずに0を返す
            return 0;
        }
        if (!endReachedThresholdRows || !rowHeight) {
            // 計算不可の場合はデフォルト値を使用する
            return DEFAULT_END_REACHED_THRESHOLD;
        }
        return endReachedThresholdRows * rowHeight;
    }, [endReachedThresholdRows, rowHeight]);
    var rootClassName = classNames(classes.root, (_a = {},
        _a[classes.blankFilling] = hasBlankFilling,
        _a[classes.scrollbar] = scrollbarVisible,
        _a[classes.notBlankFillingScrollbar] = scrollbarVisible && !hasBlankFilling,
        _a));
    return (React.createElement("div", { className: rootClassName, style: __assign({}, measureStyle) },
        React.createElement(AutoResizer, { onResize: setTableSize }, function (_a) {
            var autoWidth = _a.width, autoHeight = _a.height;
            return enabledDraggableRow ? (React.createElement(DraggableContainer, { lockAxis: "y", getContainer: getContainer, helperContainer: getHelperContainer, useDragHandle: true, onSortEnd: handleDragSortEnd },
                React.createElement(BaseTable, { ref: tableRef, fixed: true, data: renderingRows, frozenData: frozenRows, columns: columns, width: autoWidth, height: autoHeight, components: components, rowHeight: rowHeight, headerHeight: headerHeight, rowEventHandlers: rowEventHandlers, headerRenderer: headerRenderer, rowRenderer: rowRenderer, onEndReached: onEndReached, onEndReachedThreshold: endReachedThreshold }))) : (React.createElement(BaseTable, { ref: tableRef, fixed: true, data: renderingRows, frozenData: frozenRows, columns: columns, width: autoWidth, height: autoHeight, components: components, rowHeight: rowHeight, headerHeight: headerHeight, rowEventHandlers: rowEventHandlers, headerRenderer: headerRenderer, rowRenderer: rowRenderer, onEndReached: onEndReached, onEndReachedThreshold: endReachedThreshold }));
        })));
};
